import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header';
import api from '../../utils/api';
import { toLocalizedPrice, toLocalizedStatus } from '../../utils/localeUtils';
import { usePopup } from '../../context/PopupContext';
import {
  FaDollarSign, FaCheckCircle, FaClock, FaTimesCircle,
  FaSearch, FaFileInvoice, FaTimes
} from 'react-icons/fa';
import Pagination from '../../components/Pagination';
import './ConsumerPayments.css';

const PER_PAGE = 10;

const ConsumerPayments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = usePopup();

  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const ud = localStorage.getItem('user');
    if (ud) {
      try {
        const u = JSON.parse(ud);
        if (u.role !== 'consumer') { navigate('/'); return; }
        fetchPayments();
      } catch { setLoading(false); }
    } else { navigate('/login'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const res = await api.get('/payments');
      setPayments((res.data || []).map(p => ({
        ...p,
        orderId: p.order?._id || p.order || null,
        orderData: p.order?._id ? p.order : null
      })));
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else showToast(t('consumerPayments.fetchError'), 'error');
      setPayments([]);
    } finally { setLoading(false); }
  };

  const getStatusColor = (s) => {
    switch (s?.toLowerCase()) {
      case 'completed': case 'paid': return '#22c55e';
      case 'pending': return '#f59e0b';
      case 'failed': return '#ef4444';
      case 'refunded': return '#9e9e9e';
      default: return '#888';
    }
  };

  const getStatusIcon = (s) => {
    switch (s?.toLowerCase()) {
      case 'completed': case 'paid': return <FaCheckCircle />;
      case 'pending': return <FaClock />;
      case 'failed': case 'refunded': return <FaTimesCircle />;
      default: return <FaClock />;
    }
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const gwName = (g) => ({ khalti: 'Khalti', esewa: 'eSewa', ime: 'IME Pay' }[g?.toLowerCase()] || g || '—');

  const filtered = useMemo(() => payments.filter(p => {
    if (filterStatus !== 'all' && p.paymentStatus?.toLowerCase() !== filterStatus) return false;
    if (filterMethod !== 'all' && p.paymentMethod?.toLowerCase() !== filterMethod.toLowerCase()) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const match = p.transactionId?.toLowerCase().includes(q)
        || (p.orderId || p.order?._id || '').toString().toLowerCase().includes(q)
        || p.farmer?.name?.toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  }), [payments, filterStatus, filterMethod, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageItems = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  useEffect(() => { setPage(1); }, [filterStatus, filterMethod, searchQuery]);

  const stats = useMemo(() => {
    const total = payments.reduce((s, p) => s + (p.amount || 0), 0);
    const completed = payments.filter(p => ['completed', 'paid'].includes(p.paymentStatus?.toLowerCase())).reduce((s, p) => s + (p.amount || 0), 0);
    const pending = payments.filter(p => p.paymentStatus?.toLowerCase() === 'pending').reduce((s, p) => s + (p.amount || 0), 0);
    const failed = payments.filter(p => p.paymentStatus?.toLowerCase() === 'failed').reduce((s, p) => s + (p.amount || 0), 0);
    return { total, completed, pending, failed };
  }, [payments]);

  if (loading) {
    return (
      <div className="cp-page"><Header />
        <div className="cp-loading-state"><div className="cp-dots"><span /><span /><span /></div><p>{t('consumerPayments.loading')}</p></div>
      </div>
    );
  }

  return (
    <div className="cp-page">
      <Header />
      <div className="cp-container">

        {/* Header */}
        <div className="cp-header">
          <h1 className="cp-title">{t('consumerPayments.title')}</h1>
          <p className="cp-subtitle">{t('consumerPayments.subtitle')}</p>
        </div>

        {/* Stats */}
        <div className="cp-stats-grid">
          {[
            { icon: <FaDollarSign />, value: `Rs. ${toLocalizedPrice(stats.total)}`, label: t('consumerPayments.totalPayments'), cls: 'total' },
            { icon: <FaCheckCircle />, value: `Rs. ${toLocalizedPrice(stats.completed)}`, label: t('consumerPayments.completed'), cls: 'completed' },
            { icon: <FaClock />, value: `Rs. ${toLocalizedPrice(stats.pending)}`, label: t('consumerPayments.pending'), cls: 'pending' },
            { icon: <FaTimesCircle />, value: `Rs. ${toLocalizedPrice(stats.failed)}`, label: t('consumerPayments.failed'), cls: 'failed' },
          ].map(s => (
            <div key={s.cls} className="cp-stat-card">
              <div className={`cp-stat-icon ${s.cls}`}>{s.icon}</div>
              <div className="cp-stat-content">
                <div className="cp-stat-value">{s.value}</div>
                <div className="cp-stat-label">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="cp-filters">
          <div className="cp-search-box">
            <FaSearch className="cp-search-icon" />
            <input
              type="text"
              placeholder={t('consumerPayments.searchPlaceholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="cp-search-input"
            />
            {searchQuery && <button className="cp-search-clear" onClick={() => setSearchQuery('')}><FaTimes /></button>}
          </div>
          <div className="cp-filter-group">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="cp-filter-select">
              <option value="all">{t('consumerPayments.allStatus')}</option>
              <option value="completed">{t('consumerPayments.completed')}</option>
              <option value="pending">{t('consumerPayments.pending')}</option>
              <option value="failed">{t('consumerPayments.failed')}</option>
              <option value="refunded">{t('consumerPayments.refunded')}</option>
            </select>
            <select value={filterMethod} onChange={e => setFilterMethod(e.target.value)} className="cp-filter-select">
              <option value="all">{t('consumerPayments.allMethods')}</option>
              <option value="online">{t('consumerPayments.online')}</option>
              <option value="COD">{t('consumerPayments.cod')}</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="cp-table-container">
          <table className="cp-table">
            <thead>
              <tr>
                <th>{t('consumerPayments.transactionId')}</th>
                <th>{t('consumerPayments.orderId')}</th>
                <th>{t('consumerPayments.farmer')}</th>
                <th>{t('consumerPayments.amount')}</th>
                <th>{t('consumerPayments.method')}</th>
                <th>{t('consumerPayments.gateway')}</th>
                <th>{t('consumerPayments.status')}</th>
                <th>{t('consumerPayments.date')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pageItems.length === 0 ? (
                <tr><td colSpan="9" className="cp-empty-cell">
                  <FaFileInvoice className="cp-empty-icon" />
                  <p>{t('consumerPayments.noPayments')}</p>
                </td></tr>
              ) : pageItems.map(p => (
                <tr key={p._id}>
                  <td><span className="cp-txn-id">{p.transactionId || '—'}</span></td>
                  <td>
                    <Link to={`/order/${p.orderId || p.order?._id || p.order || ''}`} className="cp-order-link">
                      #{(p.orderId || p.order?._id || p.order || '').toString().slice(-8) || '—'}
                    </Link>
                  </td>
                  <td>{p.farmer?.name || '—'}</td>
                  <td className="cp-amount">Rs. {toLocalizedPrice(p.amount ?? 0)}</td>
                  <td><span className={`cp-method-badge ${p.paymentMethod?.toLowerCase()}`}>{p.paymentMethod === 'COD' ? t('consumerPayments.cod') : t('consumerPayments.online')}</span></td>
                  <td>{p.paymentMethod?.toLowerCase() === 'online' ? gwName(p.paymentGateway) : '—'}</td>
                  <td>
                    <span className="cp-status-badge" style={{ background: getStatusColor(p.paymentStatus) }}>
                      {getStatusIcon(p.paymentStatus)} {toLocalizedStatus(p.paymentStatus, t) || '—'}
                    </span>
                  </td>
                  <td className="cp-date">{fmtDate(p.paidAt || p.createdAt)}</td>
                  <td>
                    <button className="cp-btn-view" onClick={() => { setSelectedPayment(p); setShowModal(true); }}>
                      {t('consumerPayments.viewDetails')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

        {/* Detail Modal */}
        {showModal && selectedPayment && (
          <div className="cp-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="cp-modal-content" onClick={e => e.stopPropagation()}>
              <div className="cp-modal-header">
                <h2>{t('consumerPayments.paymentDetails')}</h2>
                <button className="cp-modal-close" onClick={() => setShowModal(false)}><FaTimes /></button>
              </div>
              <div className="cp-modal-body">
                <div className="cp-detail-grid-new">
                  {[
                    [t('consumerPayments.transactionId'), selectedPayment.transactionId || '—'],
                    [t('consumerPayments.paymentId'), `#${selectedPayment._id?.slice(-8)}`],
                    [t('consumerPayments.amount'), `Rs. ${toLocalizedPrice(selectedPayment.amount ?? 0)}`],
                    [t('consumerPayments.method'), selectedPayment.paymentMethod === 'COD' ? t('consumerPayments.cod') : t('consumerPayments.online')],
                    [t('consumerPayments.gateway'), selectedPayment.paymentMethod?.toLowerCase() === 'online' ? gwName(selectedPayment.paymentGateway) : '—'],
                    [t('consumerPayments.status'), toLocalizedStatus(selectedPayment.paymentStatus, t) || '—'],
                    [t('consumerPayments.paymentDate'), fmtDate(selectedPayment.paidAt || selectedPayment.createdAt)],
                    [t('consumerPayments.createdAt'), fmtDate(selectedPayment.createdAt)],
                    [t('consumerPayments.farmerName'), selectedPayment.farmer?.name || '—'],
                    [t('consumerPayments.farmerEmail'), selectedPayment.farmer?.email || '—'],
                  ].map(([label, val]) => (
                    <div key={label} className="cp-detail-cell">
                      <span className="cp-detail-label">{label}</span>
                      <span className="cp-detail-val">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="cp-modal-footer">
                <button className="cp-btn-cancel" onClick={() => setShowModal(false)}>{t('consumerPayments.close')}</button>
                {(selectedPayment.orderId || selectedPayment.order) && (
                  <Link
                    to={`/order/${selectedPayment.orderId || selectedPayment.order?._id || selectedPayment.order || ''}`}
                    className="cp-btn-view-order"
                    onClick={() => setShowModal(false)}
                  >
                    {t('consumerPayments.viewOrder')}
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsumerPayments;
